import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";

import { SocialNetworkProfiles } from "@/components";

import { SubscribedPlayerSearchResponse } from "@/interfaces";
import { URLS, usePlayerFetch, usePlayerSearch, useSegmentation, useSocialCoachSession } from "@/utils";

import { PlayersServices } from "@/services";

import { PageWrapper } from "@/components/PageWrapper";
import { useEffect } from "react";
import { Tabs } from "@/components/ui";
import {
  PlayerInfoTab,
  PlayerCustomizationsTab,
  PlayerProfile,
  PlayerLeaderboards,
} from "@/containers/Coach/EditPlayer/components";

interface MatchParams {
  playerId: string;
  tab?: string;
}
type Props = RouteComponentProps<MatchParams>;

const EditPlayerFC: React.FC<Props> = ({ match: { params }, history }) => {
  const playerId = params.playerId;
  const urlTab = params.tab;

  const [tab, setTab] = React.useState<string>(urlTab || "player-info");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [player, setPlayer] = React.useState<SubscribedPlayerSearchResponse | undefined>(undefined);

  const { coachId } = useSocialCoachSession();
  const isActivePlayer = player?.pricePlanSubscription?.status !== "CANCELED";

  const loadPlayer = async () => {
    setLoading(true);
    const player = await PlayersServices.getPlayer(coachId!, playerId);
    setPlayer(player);
    setLoading(false);
  };

  useEffect(() => {
    if (playerId) {
      loadPlayer().catch(console.error);
    }
  }, [playerId]);

  const { clearPlayerSearchCache } = usePlayerSearch();
  const { clearPlayerFetchCache } = usePlayerFetch();
  const { clearSegmentationCache } = useSegmentation();

  const clearCaches = () => {
    clearPlayerSearchCache();
    clearPlayerFetchCache();
    clearSegmentationCache();
  };

  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    history.replace(`/users/players/${playerId}/${newTab}`);
  };

  return (
    <PageWrapper
      title="Player Details"
      breadcrumbs={[{ name: "Users", link: URLS.coach.players }]}
      hideSeparator={true}
    >
      <div className="flex flex-col gap-6">
        <PlayerProfile
          playerAccount={player?.playerAccount}
          fetching={loading}
          clearCaches={clearCaches}
          reloadPlayer={loadPlayer}
        />
        <Tabs value={tab} onValueChange={handleTabChange}>
          <Tabs.List className="mb-4" variant="menu">
            <Tabs.Trigger value={"player-info"}>Player Info</Tabs.Trigger>
            <Tabs.Trigger value={"player-customizations"} disabled={!isActivePlayer}>
              Customizations
            </Tabs.Trigger>
            <Tabs.Trigger value={"player-socials"} disabled={!isActivePlayer}>
              Social Accounts
            </Tabs.Trigger>
            <Tabs.Trigger value={"player-stats"} disabled={!isActivePlayer}>
              Stats
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="player-info">
            {player && <PlayerInfoTab player={player} reloadPlayer={loadPlayer} clearCaches={clearCaches} />}
          </Tabs.Content>

          <Tabs.Content value="player-customizations">
            {player && <PlayerCustomizationsTab player={player} reloadPlayer={loadPlayer} clearCaches={clearCaches} />}
          </Tabs.Content>

          <Tabs.Content value="player-socials">
            {player && <SocialNetworkProfiles player={player.playerAccount} />}
          </Tabs.Content>

          <Tabs.Content value="player-stats">
            {player?.playerAccount?.playerId && <PlayerLeaderboards playerId={player.playerAccount.playerId} />}
          </Tabs.Content>
        </Tabs>
      </div>
    </PageWrapper>
  );
};

export const EditPlayer = withRouter(EditPlayerFC);
